import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, firstValueFrom } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { CryptoService } from '@services/crypto/crypto.service';
import { StorageService } from '../storage/storage.service';
import { sleep } from '../utils';

export enum eAuthProvider
{
    HyperID = 'HYPER-ID'
}

@Injectable({
    providedIn: 'root'
})
export class AuthService
{

    private loginState_: ReplaySubject<boolean> = new ReplaySubject(1);
    private lockState_: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(
        private http: HttpClient,
        private router: Router,
        private translateService: TranslateService,
        private storage: StorageService,
        private cryptoService: CryptoService
    )
    {
        const isExternal = location.pathname.includes('/create-external');
        this.checkLoggedIn(isExternal);
        window.addEventListener('storage', e =>
        {
            switch (e.key)
            {
                case 'is_logged_in':
                    if (e.newValue == 'true')
                        this.checkLoggedIn();
                    if (e.newValue == 'false')
                        this.logOut(false);
                    break;

                case 'is_locked':
                    if (e.newValue == 'true')
                        this.lock(false);
                    if (e.newValue == 'false')
                        this.checkUnlocked();
                    break;
            }
        });
    }

    get loginState(): Observable<boolean>
    {
        return this.loginState_.asObservable();
    }

    public async currentLoginState(): Promise<boolean>
    {
        return this.getValue(this.loginState);
    }

    get lockState(): Observable<boolean>
    {
        return this.lockState_.asObservable();
    }

    private async readUserKey()
    {
        let keyBase64 = null;
        do
        {
            keyBase64 = await this.storage.get('userkey_publicBase64');
        }
        while (keyBase64 == null);

        return encodeURIComponent(keyBase64);
    }

    public authorize(_scopes = [])
    {
        const lang = this.translateService.currentLang;
        location.href = `/auth/oauth2?lang=${ lang }&scopes=${ JSON.stringify(_scopes) }`;
    }

    public async clearCookies()
    {
        const res = await firstValueFrom(this.http.get(`/api/session/log-out?full=${ false }`));
        console.log("🚀 ~ res:", res);
    }

    public async readAuthorizeUrl(_scopes = [])
    {
        const lang = this.translateService.currentLang;
        let url = `/auth/oauth2?lang=${ lang }&scopes=${ JSON.stringify(_scopes) }`;
        const response = await this.http.post(url, {}).toPromise();

        const encodedUrl = response[ 'encodedBase64Url' ];
        const buffUrl = Buffer.from(encodedUrl, 'base64');
        const decodedUrl = buffUrl.toString();

        const userKey = await this.readUserKey();

        url = `${ decodedUrl }&lang=${ lang }&user_key=${ userKey }`;
        console.log(`<< /sign-in/oauth2 ${ url }`);
        return url;
    }

    public async processCallback(_queryParams: Partial<Record<string, any>>)
    {
        const res = await this.http.post('/auth/process-callback', _queryParams).toPromise();
        return res[ 'url' ];
    }

    public async checkLoggedIn(_external = false)
    {
        try
        {
            if (!_external)
            {
                await this.clearExternalSession();
            }

            const response = await firstValueFrom(this.http.get('/api/session/check', { responseType: 'text' }));
            console.log("🚀 ~ response:", response, Boolean(response));

            if (String(response) === 'true')
            {
                this.loginState_.next(true);
                localStorage.setItem('is_logged_in', 'true');

                if (_external)
                {
                    return true;
                }
                else if (!await this.checkUserSecret())
                {
                    await this.logOut(false, true, 'Sign in timeframe is over, please refresh the page and try again');
                    return false;
                }

                return true;
            }
            else
            {
                this.loginState_.next(false);
                localStorage.setItem('is_logged_in', 'false');
            }
        } catch (error)
        {
            console.log(error);
            this.loginState_.next(false);
            localStorage.setItem('is_logged_in', 'false');
        }

        return false;
    }

    public async checkUnlocked(): Promise<boolean>
    {
        try
        {
            const loginState = await this.currentLoginState();
            if (!loginState) return false;

            const response = await this.http.get('/api/session/check-unlocked').toPromise();
            const isUnlocked = Boolean(response);

            this.lockState_.next(!isUnlocked);
            return isUnlocked;
        } catch (error)
        {
            console.log(error);
        }

        return false;
    }

    public async lock(_redirect = true)
    {
        var actionData;
        console.log('lock session');
        try
        {
            if (_redirect)
            {
                actionData = await this.http.get('/api/session/lock').toPromise();
            }

            switch (actionData[ 'result' ])
            {
                case 'locked':
                    this.lockState_.next(true);
                    localStorage.setItem('is_locked', 'true');
                    this.router.navigate([ '/unlock' ]);
                    break;
                case 'logout':
                    this.loginState_.next(false);
                    localStorage.removeItem('is_locked');
                    localStorage.removeItem('auth_type');
                    localStorage.setItem('is_logged_in', 'false');
                    this.router.navigate([ '/sign-in' ], { queryParamsHandling: 'merge' });
                    break;
            }
        }
        catch (error)
        {
            console.log(error);
            this.loginState_.next(false);
        }
    }

    public async logOut(_redirect = true, _failed = false, _errorMessage?: string)
    {

        let signInUrl = _failed ? '/sign-in/failed' : '/sign-in';

        const queryParams = {};

        if (_failed)
        {
            queryParams[ 'message' ] = _errorMessage;
        }

        try
        {
            await this.http.get(`/api/session/log-out?full=${ _redirect }`).toPromise();

            this.loginState_.next(false);
            localStorage.removeItem('is_locked');
            localStorage.removeItem('auth_type');
            localStorage.setItem('is_logged_in', 'false');
            this.router.navigate([ signInUrl ], { queryParams: queryParams, queryParamsHandling: 'merge' });

        } catch (error)
        {
            this.loginState_.next(false);
            this.router.navigate([ signInUrl ], { queryParams: queryParams, queryParamsHandling: 'merge' });
        }
    }

    public async setExternalSession(_accessToken: string)
    {
        await this.http.post('/auth/setup-external-session', { access_token: _accessToken }).toPromise();
    }

    public async clearExternalSession()
    {
        await this.http.post('/auth/clear-external-session', {}).toPromise();
    }

    public async logoutAndClearAll()
    {
        localStorage.clear();
        this.storage.clearAll();
        await this.logOut(true);
    }

    private async checkUserSecret(attempt_: number = 1): Promise<boolean>
    {
        const secretKey = await this.cryptoService.hasSecret();

        if (!secretKey)
        {
            if (attempt_ <= 5)
            {
                await sleep(1000);
                return await this.checkUserSecret(attempt_ + 1);
            }

            return false;
        }

        return true;
    }

    private getValue<T>(observable: Observable<T>): Promise<T>
    {
        return observable
            .pipe(
                filter(value => value !== null && value !== undefined),
                first()
            )
            .toPromise();
    }
}
